import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para BIEs (Bocas de Incendio Equipadas) e Hidrantes.
 *
 * Referencias normativas:
 *   - RD 513/2017 (Reglamento de Instalaciones de Protección contra Incendios), Anexo II, Tabla I.
 *   - UNE-EN 671-3 (mantenimiento de BIEs).
 *   - UNE 23405 / UNE 23406 (hidrantes de columna y arqueta).
 *   - UNE-EN 14384 / UNE-EN 14339.
 *
 * Periodicidad:
 *   T = Trimestral
 *   A = Anual (incluye prueba de presión y estanqueidad)
 *   La prueba hidráulica de manguera se realiza cada 5 años → marcada como 'A' (se controla en campo de fecha).
 */
export const BIES_HIDRANTES_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'bies_hidrantes',
  titulo: 'Sistema de BIEs e Hidrantes',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y distribución',
      cabecera: [
        { key: 'num_bies_25', label: 'Nº BIEs 25 mm', tipo: 'number' },
        { key: 'num_bies_45', label: 'Nº BIEs 45 mm', tipo: 'number' },
        { key: 'num_hidrantes', label: 'Nº hidrantes', tipo: 'number' },
        { key: 'tipo_red', label: 'Tipo de red (anillada / ramificada)' },
        { key: 'presion_estatica_red', label: 'Presión estática red (bar)' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿Dispone cada BIE/hidrante de etiqueta de revisión por empresa mantenedora visible y legible?', periodicidad: ['T','A'], unidades: true },
        { id: 'IN.02', texto: '¿Coincide el inventario físico con planos / listado del cliente?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿La distancia entre BIEs (≤ 50 m, máx. recorrido 25 m) cumple lo exigido por el RD 513/2017?', periodicidad: ['A'] },
        { id: 'IN.04', texto: '¿La separación entre hidrantes exteriores (≤ 100 m en zonas urbanas / ≤ 200 m industriales) es la reglamentaria?', periodicidad: ['A'] },
        { id: 'IN.05', texto: '¿Existen BIEs / hidrantes específicos junto a riesgos especiales del edificio?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'bie_estado',
      titulo: '2. Estado físico y accesibilidad (BIE)',
      preguntas: [
        { id: 'BE.01', texto: '¿El armario está libre de obstáculos, accesible y debidamente señalizado (UNE 23033)?', periodicidad: ['T','A'], unidades: true },
        { id: 'BE.02', texto: '¿La puerta del armario abre correctamente y permite el despliegue total de la manguera sin obstáculos?', periodicidad: ['T','A'], unidades: true },
        { id: 'BE.03', texto: '¿El cristal (si lo tiene) está íntegro y dispone de martillo rompecristales?', periodicidad: ['T','A'], unidades: true },
        { id: 'BE.04', texto: '¿El estado externo del armario (pintura, oxidación, golpes, fijación) es correcto?', periodicidad: ['T','A'], unidades: true },
        { id: 'BE.05', texto: '¿Las instrucciones de uso están visibles, en castellano y legibles?', periodicidad: ['T','A'], unidades: true },
        { id: 'BE.06', texto: '¿La altura de la válvula (≤ 1,50 m del suelo) cumple normativa?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'bie_componentes',
      titulo: '3. Componentes de la BIE',
      preguntas: [
        { id: 'BC.01', texto: '¿El estado de la manguera (sin grietas, dobleces, abrasiones, hongos ni rigidez excesiva) es correcto?', periodicidad: ['T','A'], unidades: true },
        { id: 'BC.02', texto: '¿Los racores y la unión a la lanza están en buen estado y sin pérdidas?', periodicidad: ['T','A'], unidades: true },
        { id: 'BC.03', texto: '¿La lanza (chorro/pulverización/cierre) funciona en todas sus posiciones?', periodicidad: ['T','A'], unidades: true },
        { id: 'BC.04', texto: '¿La válvula de paso abre y cierra sin resistencia anómala ni fugas?', periodicidad: ['T','A'], unidades: true },
        { id: 'BC.05', texto: '¿El soporte (devanadera o plegado en zigzag) gira/despliega correctamente?', periodicidad: ['T','A'], unidades: true },
        { id: 'BC.06', texto: '¿El manómetro indica la presión de la red en reposo y está dentro de zona admisible?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'bie_pruebas',
      titulo: '4. Pruebas hidráulicas y de funcionamiento (BIE)',
      cabecera: [
        { key: 'presion_servicio', label: 'Presión de servicio medida (bar)' },
        { key: 'presion_prueba', label: 'Presión de prueba aplicada (bar)' },
        { key: 'fecha_prueba_5a', label: 'Fecha última prueba 5 años', tipo: 'date' },
      ],
      preguntas: [
        { id: 'BP.01', texto: '¿Se ha desplegado totalmente la manguera y comprobado que no hay dobleces ni puntos críticos?', periodicidad: ['A'], unidades: true },
        { id: 'BP.02', texto: '¿Se ha realizado prueba de funcionamiento abriendo la BIE y comprobando presión dinámica en punta de lanza (≥ 2 bar y ≤ 5 bar en BIE 25; ≥ 3,5 bar en BIE 45)?', periodicidad: ['A'], unidades: true },
        { id: 'BP.03', texto: '¿Se ha verificado la estanqueidad de la válvula, racores y manguera sin pérdidas?', periodicidad: ['A'], unidades: true },
        { id: 'BP.04', texto: '¿Está dentro del plazo la prueba hidráulica quinquenal de la manguera (15 bar / 5 minutos) sin deformación ni rotura?', periodicidad: ['A'], unidades: true, nota: 'UNE-EN 671-3: cada 5 años.' },
        { id: 'BP.05', texto: '¿Se ha recolocado la manguera correctamente plegada/devanada tras la prueba?', periodicidad: ['A'], unidades: true },
        { id: 'BP.06', texto: '¿Las mangueras superan la vida útil establecida por el fabricante (habitualmente 20 años)?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'hidrantes',
      titulo: '5. Hidrantes (columna y arqueta)',
      cabecera: [
        { key: 'tipo_hidrante', label: 'Tipo (columna seca / húmeda / arqueta)' },
        { key: 'diametro_salida', label: 'Diámetro salidas (mm)' },
        { key: 'presion_dinamica', label: 'Presión dinámica medida (bar)' },
        { key: 'caudal_medido', label: 'Caudal medido (l/min)' },
      ],
      preguntas: [
        { id: 'HI.01', texto: '¿El hidrante está accesible, libre de obstáculos (vehículos, vegetación, contenedores) y correctamente señalizado?', periodicidad: ['T','A'], unidades: true },
        { id: 'HI.02', texto: '¿La tapa de la arqueta o el cuerpo de la columna están en buen estado y sin deterioro?', periodicidad: ['T','A'], unidades: true },
        { id: 'HI.03', texto: '¿Las bocas de salida y los racores Barcelona / Storz están sin daños y con tapones y cadenas?', periodicidad: ['T','A'], unidades: true },
        { id: 'HI.04', texto: '¿La maniobra de apertura (volante / llave) gira sin atascos y no presenta fugas externas?', periodicidad: ['T','A'], unidades: true },
        { id: 'HI.05', texto: '¿Se ha engrasado el husillo y se ha realizado mantenimiento de juntas según fabricante?', periodicidad: ['A'], unidades: true },
        { id: 'HI.06', texto: 'En hidrantes de columna seca, ¿el drenaje funciona correctamente al cerrar (evitando heladas)?', periodicidad: ['A'], unidades: true },
        { id: 'HI.07', texto: '¿Se ha realizado prueba de presión dinámica abriendo el hidrante (≥ 5 bar a caudal de 1.000 l/min)?', periodicidad: ['A'], unidades: true },
        { id: 'HI.08', texto: '¿Se han comprobado las juntas y se han cambiado las que estaban deterioradas en el último mantenimiento?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'red_general',
      titulo: '6. Red general y abastecimiento',
      preguntas: [
        { id: 'RG.01', texto: '¿La red de tubería (visible) presenta buen estado, sin corrosión, fugas ni golpes?', periodicidad: ['A'] },
        { id: 'RG.02', texto: '¿Las válvulas de seccionamiento de la red están abiertas, accesibles y debidamente identificadas?', periodicidad: ['A'] },
        { id: 'RG.03', texto: '¿El depósito o aljibe que alimenta la red dispone de la capacidad mínima exigida por proyecto?', periodicidad: ['A'] },
        { id: 'RG.04', texto: '¿La presión y caudal en el punto hidráulicamente más desfavorable cumple lo exigido por el RD 513/2017?', periodicidad: ['A'] },
        { id: 'RG.05', texto: '¿Está la red protegida contra heladas en zonas expuestas?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '7. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión anual realizada por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Se conservan los certificados de las pruebas hidráulicas quinquenales de mangueras de los últimos 5 años?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Está actualizado el inventario con marca, modelo, ubicación, nº de serie, fechas de fabricación, retimbre y caducidad de cada BIE/hidrante?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Se ha entregado al titular el informe con anomalías detectadas y acciones correctoras propuestas?', periodicidad: ['A'] },
      ],
    },
  ],
};
